import { Fragment, createElement, useCallback, useEffect, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Divider as AntdDivider, Dropdown, Select, Space, Tooltip, message } from 'antd';
import type { MenuProps } from 'antd';
import {
    BoldOutlined,
    ItalicOutlined,
    UnderlineOutlined,
    StrikethroughOutlined,
    OrderedListOutlined,
    UnorderedListOutlined,
    AlignLeftOutlined,
    AlignCenterOutlined,
    AlignRightOutlined,
    MenuOutlined,
    TableOutlined,
    LinkOutlined,
    DisconnectOutlined,
    UndoOutlined,
    RedoOutlined,
    ClearOutlined,
    AppstoreAddOutlined,
} from '@ant-design/icons';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import Image from '@tiptap/extension-image';
import { TextStyle, FontFamily, FontSize } from '@tiptap/extension-text-style';
import {
    ClassSpan,
    PreserveClassAttributes,
    StructuralArticle,
    StructuralDiv,
    StructuralFooter,
    StructuralHeader,
    StructuralSection,
} from './richTextEditorExtensions';
import './RichTextEditor.css';

/**
 * Variable group descriptor used to build the "Insert variable" menu.
 * Each variable resolves to a Handlebars expression like `{{Contract.Nr}}`
 * which the backend render service expands when generating the PDF.
 */
export interface VariableGroup {
    key: string;
    label: string;
    variables: Array<{ token: string; label: string }>;
}

interface RichTextEditorProps {
    value: string;
    onChange: (html: string) => void;
    variableGroups: VariableGroup[];
    onInsertObjectsTable?: () => void;
    disabled?: boolean;
}

const FONT_FAMILY_OPTIONS = [
    { value: '', label: 'Default' },
    { value: "'DejaVu Sans', Arial, sans-serif", label: 'DejaVu Sans' },
    { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
    { value: "'Times New Roman', Times, serif", label: 'Times New Roman' },
    { value: 'Georgia, serif', label: 'Georgia' },
    { value: "'Courier New', Courier, monospace", label: 'Courier New' },
    { value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
    { value: "'Trebuchet MS', sans-serif", label: 'Trebuchet MS' },
    { value: 'Tahoma, Geneva, sans-serif', label: 'Tahoma' },
];

const FONT_SIZE_OPTIONS = [
    { value: '', label: 'Default' },
    { value: '8pt', label: '8' },
    { value: '9pt', label: '9' },
    { value: '10pt', label: '10' },
    { value: '11pt', label: '11' },
    { value: '12pt', label: '12' },
    { value: '14pt', label: '14' },
    { value: '16pt', label: '16' },
    { value: '18pt', label: '18' },
    { value: '20pt', label: '20' },
    { value: '24pt', label: '24' },
    { value: '28pt', label: '28' },
    { value: '32pt', label: '32' },
];

const MONOSPACE_STYLE: CSSProperties = { fontFamily: 'monospace' };
const LABEL_HINT_STYLE: CSSProperties = { color: '#888' };
const EDITOR_CONTAINER_STYLE: CSSProperties = { border: '1px solid #d9d9d9', borderRadius: 4 };
const EDITOR_CONTENT_STYLE: CSSProperties = {
    padding: '8px 12px',
    minHeight: 380,
    maxHeight: '55vh',
    overflow: 'auto',
    background: '#fff',
};

const RichTextEditor = ({
    value,
    onChange,
    variableGroups,
    onInsertObjectsTable,
    disabled,
}: RichTextEditorProps) => {
    const { t } = useTranslation();

    const editor = useEditor({
        editable: !disabled,
        extensions: [
            StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
            Underline,
            Link.configure({ openOnClick: false, autolink: true }),
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Table.configure({ resizable: false, HTMLAttributes: { class: 'contract-table' } }),
            TableRow,
            TableHeader,
            TableCell,
            Image,
            TextStyle,
            FontFamily.configure({ types: ['textStyle'] }),
            FontSize.configure({ types: ['textStyle'] }),
            // Preserve structural wrappers and class/style attributes so the
            // contract template can round-trip through the editor without
            // losing the CSS hooks it depends on.
            StructuralSection,
            StructuralDiv,
            StructuralHeader,
            StructuralFooter,
            StructuralArticle,
            ClassSpan,
            PreserveClassAttributes,
        ],
        content: value,
        onUpdate: ({ editor: instance }) => {
            const html = instance.getHTML();
            onChange(html);
        },
    });

    // Keep the editor content in sync when the parent re-loads a template
    // (e.g. after "Restore default" or initial fetch). We only push content
    // back into the editor when it actually differs from the current value
    // to avoid wiping selection and undo history during normal typing.
    useEffect(() => {
        if (!editor) return;
        const current = editor.getHTML();
        if (value && value !== current) {
            editor.commands.setContent(value, { emitUpdate: false });
        }
    }, [editor, value]);

    useEffect(() => {
        if (!editor) return;
        // Pass `emitUpdate = false` so toggling the editable flag does NOT
        // fire a synthetic `update` event. Without this, the first effect
        // run (right after the editor is created) would emit an update with
        // TipTap's normalized HTML — silently overwriting the parent's
        // original `htmlContent` state with a lossy version that drops
        // custom wrappers (e.g. `<section>`, `<div class="contract-...">`)
        // and breaks the rendered template CSS on the next preview.
        editor.setEditable(!disabled, false);
    }, [editor, disabled]);

    const insertText = useCallback(
        (text: string) => {
            if (!editor) return;
            editor.chain().focus().insertContent(text).run();
        },
        [editor],
    );

    const buildVariableGroupItem = useCallback(
        (group: VariableGroup) => ({
            key: group.key,
            label: group.label,
            children: group.variables.map(v => ({
                key: v.token,
                label: createElement(Fragment, null, [
                    createElement('bdi', { key: 'token', style: MONOSPACE_STYLE }, v.token),
                    createElement(
                        'bdi',
                        { key: 'label', style: LABEL_HINT_STYLE },
                        ` \u2014 ${v.label}`,
                    ),
                ]),
                onClick: () => insertText(v.token),
            })),
        }),
        [insertText],
    );

    const variableMenu = useMemo<MenuProps>(
        () => ({ items: variableGroups.map(buildVariableGroupItem) }),
        [variableGroups, buildVariableGroupItem],
    );

    const setLink = useCallback(() => {
        if (!editor) return;
        const previousUrl = editor.getAttributes('link').href as string | undefined;
        // eslint-disable-next-line no-alert
        const url = window.prompt(
            t('configuration.contractDocument.toolbar.linkPrompt'),
            previousUrl ?? '',
        );
        if (url === null) return;
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }, [editor, t]);

    if (!editor) {
        return null;
    }

    const renderToolbar = () => (
        <Space
            wrap
            size={4}
            style={{ padding: 6, borderBottom: '1px solid #d9d9d9', background: '#fafafa' }}
        >
            <Tooltip title={t('configuration.contractDocument.toolbar.fontFamily')}>
                <Select
                    size="small"
                    style={{ width: 140 }}
                    value={(editor.getAttributes('textStyle').fontFamily as string) ?? ''}
                    onChange={family => {
                        if (!family) {
                            editor.chain().focus().unsetFontFamily().run();
                        } else {
                            editor.chain().focus().setFontFamily(family).run();
                        }
                    }}
                    options={FONT_FAMILY_OPTIONS}
                    placeholder={t('configuration.contractDocument.toolbar.fontFamily')}
                />
            </Tooltip>
            <Tooltip title={t('configuration.contractDocument.toolbar.fontSize')}>
                <Select
                    size="small"
                    style={{ width: 80 }}
                    value={(editor.getAttributes('textStyle').fontSize as string) ?? ''}
                    onChange={size => {
                        if (!size) {
                            editor.chain().focus().unsetFontSize().run();
                        } else {
                            editor.chain().focus().setFontSize(size).run();
                        }
                    }}
                    options={FONT_SIZE_OPTIONS}
                    placeholder={t('configuration.contractDocument.toolbar.fontSize')}
                />
            </Tooltip>
            <Divider />
            <ToolbarButton
                tooltip={t('configuration.contractDocument.toolbar.bold')}
                icon={<BoldOutlined />}
                active={editor.isActive('bold')}
                onClick={() => editor.chain().focus().toggleBold().run()}
            />
            <ToolbarButton
                tooltip={t('configuration.contractDocument.toolbar.italic')}
                icon={<ItalicOutlined />}
                active={editor.isActive('italic')}
                onClick={() => editor.chain().focus().toggleItalic().run()}
            />
            <ToolbarButton
                tooltip={t('configuration.contractDocument.toolbar.underline')}
                icon={<UnderlineOutlined />}
                active={editor.isActive('underline')}
                onClick={() => editor.chain().focus().toggleUnderline().run()}
            />
            <ToolbarButton
                tooltip={t('configuration.contractDocument.toolbar.strike')}
                icon={<StrikethroughOutlined />}
                active={editor.isActive('strike')}
                onClick={() => editor.chain().focus().toggleStrike().run()}
            />
            <Divider />
            <Dropdown
                menu={{
                    items: [
                        {
                            key: 'p',
                            label: t('configuration.contractDocument.toolbar.paragraph'),
                            onClick: () => editor.chain().focus().setParagraph().run(),
                        },
                        {
                            key: 'h1',
                            label: t('configuration.contractDocument.toolbar.heading1'),
                            onClick: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
                        },
                        {
                            key: 'h2',
                            label: t('configuration.contractDocument.toolbar.heading2'),
                            onClick: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
                        },
                        {
                            key: 'h3',
                            label: t('configuration.contractDocument.toolbar.heading3'),
                            onClick: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
                        },
                    ],
                }}
            >
                <Button size="small">{`${t('configuration.contractDocument.toolbar.paragraph')} \u25BE`}</Button>
            </Dropdown>
            <ToolbarButton
                tooltip={t('configuration.contractDocument.toolbar.bulletList')}
                icon={<UnorderedListOutlined />}
                active={editor.isActive('bulletList')}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
            />
            <ToolbarButton
                tooltip={t('configuration.contractDocument.toolbar.orderedList')}
                icon={<OrderedListOutlined />}
                active={editor.isActive('orderedList')}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
            />
            <Divider />
            <ToolbarButton
                tooltip={t('configuration.contractDocument.toolbar.alignLeft')}
                icon={<AlignLeftOutlined />}
                active={editor.isActive({ textAlign: 'left' })}
                onClick={() => editor.chain().focus().setTextAlign('left').run()}
            />
            <ToolbarButton
                tooltip={t('configuration.contractDocument.toolbar.alignCenter')}
                icon={<AlignCenterOutlined />}
                active={editor.isActive({ textAlign: 'center' })}
                onClick={() => editor.chain().focus().setTextAlign('center').run()}
            />
            <ToolbarButton
                tooltip={t('configuration.contractDocument.toolbar.alignRight')}
                icon={<AlignRightOutlined />}
                active={editor.isActive({ textAlign: 'right' })}
                onClick={() => editor.chain().focus().setTextAlign('right').run()}
            />
            <ToolbarButton
                tooltip={t('configuration.contractDocument.toolbar.alignJustify')}
                icon={<MenuOutlined />}
                active={editor.isActive({ textAlign: 'justify' })}
                onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            />
            <Divider />
            <Dropdown
                menu={{
                    items: [
                        {
                            key: 'insert',
                            label: t('configuration.contractDocument.toolbar.insertTable'),
                            onClick: () =>
                                editor
                                    .chain()
                                    .focus()
                                    .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                                    .run(),
                        },
                        {
                            key: 'addRow',
                            label: t('configuration.contractDocument.toolbar.addRow'),
                            onClick: () => editor.chain().focus().addRowAfter().run(),
                        },
                        {
                            key: 'delRow',
                            label: t('configuration.contractDocument.toolbar.deleteRow'),
                            onClick: () => editor.chain().focus().deleteRow().run(),
                        },
                        {
                            key: 'addCol',
                            label: t('configuration.contractDocument.toolbar.addColumn'),
                            onClick: () => editor.chain().focus().addColumnAfter().run(),
                        },
                        {
                            key: 'delCol',
                            label: t('configuration.contractDocument.toolbar.deleteColumn'),
                            onClick: () => editor.chain().focus().deleteColumn().run(),
                        },
                        {
                            key: 'delTable',
                            label: t('configuration.contractDocument.toolbar.deleteTable'),
                            onClick: () => editor.chain().focus().deleteTable().run(),
                        },
                    ],
                }}
            >
                <Tooltip title={t('configuration.contractDocument.toolbar.insertTable')}>
                    <Button size="small" icon={<TableOutlined />} />
                </Tooltip>
            </Dropdown>
            <ToolbarButton
                tooltip={t('configuration.contractDocument.toolbar.link')}
                icon={<LinkOutlined />}
                active={editor.isActive('link')}
                onClick={setLink}
            />
            <ToolbarButton
                tooltip={t('configuration.contractDocument.toolbar.unlink')}
                icon={<DisconnectOutlined />}
                onClick={() => editor.chain().focus().unsetLink().run()}
            />
            <Divider />
            <Dropdown menu={variableMenu} trigger={['click']}>
                <Button size="small" type="primary" ghost icon={<AppstoreAddOutlined />}>
                    {t('configuration.contractDocument.insertVariable')} ▾
                </Button>
            </Dropdown>
            {onInsertObjectsTable ? (
                <Tooltip title={t('configuration.contractDocument.insertObjectsTable')}>
                    <Button
                        size="small"
                        onClick={() => {
                            onInsertObjectsTable();
                            insertText('{{ObjectsTable}}');
                            message.success(t('configuration.contractDocument.insertObjectsTable'));
                        }}
                    >
                        {'{{ObjectsTable}}'}
                    </Button>
                </Tooltip>
            ) : null}
            <Divider />
            <ToolbarButton
                tooltip={t('configuration.contractDocument.toolbar.clearFormatting')}
                icon={<ClearOutlined />}
                onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
            />
            <ToolbarButton
                tooltip={t('configuration.contractDocument.toolbar.undo')}
                icon={<UndoOutlined />}
                onClick={() => editor.chain().focus().undo().run()}
            />
            <ToolbarButton
                tooltip={t('configuration.contractDocument.toolbar.redo')}
                icon={<RedoOutlined />}
                onClick={() => editor.chain().focus().redo().run()}
            />
        </Space>
    );

    return (
        <div className="contract-rich-editor" style={EDITOR_CONTAINER_STYLE}>
            {renderToolbar()}
            <div style={EDITOR_CONTENT_STYLE}>
                <EditorContent editor={editor} />
            </div>
        </div>
    );
};

interface ToolbarButtonProps {
    tooltip: string;
    icon: React.ReactNode;
    active?: boolean;
    onClick: () => void;
}

const ToolbarButton = ({ tooltip, icon, active, onClick }: ToolbarButtonProps) => (
    <Tooltip title={tooltip}>
        <Button size="small" type={active ? 'primary' : 'default'} icon={icon} onClick={onClick} />
    </Tooltip>
);

const Divider = () =>
    createElement(AntdDivider, { className: 'contract-rich-editor-divider', type: 'vertical' });

export type { Editor };
export default RichTextEditor;
